import { useAuth } from "@/components/auth/auth-context";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useActiveCall } from "@/lib/api/calls";
import { cn } from "@/lib/utils";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useEvents } from "@/lib/hooks/use-events";
import { CallEventPayload } from "@/lib/types/events";

const NOTIFICATION_COOLDOWN_KEY = "call_notification_cooldown";
const COOLDOWN_DURATION = 60 * 1000; // 60 seconds in milliseconds

export function useActiveCallNotification(otherUserId?: string) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { toast, dismiss } = useToast();
  const { data: activeCall } = useActiveCall();
  const { addEventListener } = useEvents();

  const notificationSound = useRef<HTMLAudioElement>(
    new Audio("/notification.mp3"),
  );
  const toastRef = useRef<string | null>(null);

  const isCallActive = !!activeCall && activeCall?.toUser?.id === user?.id;

  const isCallActiveWithOtherUser =
    !!activeCall &&
    (activeCall?.toUser?.id === otherUserId ||
      activeCall?.fromUser?.id === otherUserId);

  const shouldShowCallNotification =
    isCallActive && !pathname.includes("/calls/");

  const isInCooldownPeriod = () => {
    const cooldownTimestamp = localStorage.getItem(NOTIFICATION_COOLDOWN_KEY);
    if (!cooldownTimestamp) return false;

    const timeSinceLastDismiss = Date.now() - parseInt(cooldownTimestamp);
    return timeSinceLastDismiss < COOLDOWN_DURATION;
  };

  const startCooldown = () => {
    localStorage.setItem(NOTIFICATION_COOLDOWN_KEY, Date.now().toString());
  };

  const handleNavigateToCall = () => {
    if (toastRef.current) {
      dismiss(toastRef.current);
      toastRef.current = null;
    }
    navigate({
      to: "/calls/$roomId",
      params: { roomId: activeCall?.sendbird_room_id || "" },
    });
  };

  const playNotificationSound = () => {
    notificationSound.current.play().catch((error: Error) => {
      // Ignore autoplay restriction errors
      if (error.name === "NotAllowedError") {
        console.log("Autoplay prevented - waiting for user interaction");
      } else {
        console.error("Error playing notification:", error);
      }
    });
  };

  const showCallNotification = () => {
    // If there's an existing toast or we're in cooldown, don't show another one
    if (toastRef.current || isInCooldownPeriod()) return;

    const { id } = toast({
      id: `active-call-${activeCall?.id}`,
      title: `You’re getting a call from ${activeCall?.fromUser?.name}`,
      className: cn(
        "top-0 right-0 flex fixed md:max-w-[420px] md:top-4 md:right-4",
        "border-2 border-primary shadow-lg rounded-lg",
        "bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
      ),
      description: `Click here to join call`,
      duration: Infinity, // Make it persistent
      action: (
        <Button onClick={handleNavigateToCall} variant="default">
          Go to Call
        </Button>
      ),
      onOpenChange: (open: boolean) => {
        if (!open) {
          toastRef.current = null;
          startCooldown();
        } else {
          console.log("Toast opened");
          playNotificationSound();
        }
      },
    });

    toastRef.current = id;
  };

  useEffect(() => {
    // Listen for new calls
    const cleanup = addEventListener<CallEventPayload>(
      "CALL_CREATED",
      (payload) => {
        if (payload.toUserId === user?.id && !pathname.includes("/calls/")) {
          showCallNotification();
        }
      },
    );

    // Listen for ended calls
    const cleanupEndCall = addEventListener<CallEventPayload>(
      "CALL_ENDED",
      () => {
        if (toastRef.current) {
          dismiss(toastRef.current);
          toastRef.current = null;
        }
      },
    );

    return () => {
      cleanup();
      cleanupEndCall();
    };
  }, [addEventListener, user?.id, pathname]);

  // Keep the existing effect for initial state
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (shouldShowCallNotification) {
        showCallNotification();
      } else {
        toastRef.current = null;
      }
    }, 2000);

    return () => clearTimeout(timeout);
  }, [shouldShowCallNotification]);

  return {
    isCallActive,
    isCallActiveWithOtherUser,
  };
}
